import React from "react";
import moment from "moment";
import ReactTable from "react-table";
import "react-table/react-table.css";
import matchSorter from "match-sorter";
import "./ReporteComprasSire.css";

const QUERY_DELAY_MS = 2000;
const primaryColor = "rgb(53, 96, 90)";
const secondaryColor = "rgb(1, 165, 156)";
const lightColor = "rgba(53, 96, 90, 0.08)";

class ReporteComprasSire extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            periodoCompras: moment().format("YYYYMM"),
            loadingCompras: false,
            errorCompras: "",
            compras: [],
            totalCompras: 0,
        };
    }

    componentDidMount() {
        this.consultarCompras();
    }


    monthToPeriodo = (monthValue) => {
        if (!monthValue || monthValue.length !== 7) return "";
        return monthValue.replace("-", "");
    }

    periodoToMonth = (periodo) => {
        if (!periodo || String(periodo).length !== 6) return "";
        return `${String(periodo).slice(0, 4)}-${String(periodo).slice(4, 6)}`;
    }

    fetchSire = async (tipo, periodo) => {
        const response = await fetch(`/api/sire/sunat/${tipo}/${periodo}`);
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            const msg = data && data.msg ? data.msg : `Error consultando ${tipo}`;
            throw new Error(msg);
        }

        return data;
    }

    consultarCompras = async () => {
        const { periodoCompras } = this.state;

        if (!/^\d{6}$/.test(periodoCompras)) {
            this.setState({ errorCompras: "Periodo de compras invalido. Formato: YYYYMM" });
            return;
        }

        this.setState({ loadingCompras: true, errorCompras: "" });

        try {
            await new Promise(resolve => setTimeout(resolve, QUERY_DELAY_MS));
            const data = await this.fetchSire("compras", periodoCompras);

            this.setState({
                compras: Array.isArray(data.data) ? data.data : [],
                totalCompras: Number(data.total_registros || 0),
                loadingCompras: false,
            });
        } catch (error) {
            this.setState({
                loadingCompras: false,
                errorCompras: error.message || "No se pudo consultar compras",
                compras: [],
                totalCompras: 0,
            });
        }
    }

    formatMoney = (value, currency) => {
        const safeCurrency = currency === "USD" ? "USD" : "PEN";
        const num = Number(value || 0);

        return new Intl.NumberFormat("es-PE", {
            style: "currency",
            currency: safeCurrency,
            minimumFractionDigits: 2,
        }).format(num);
    }


    getResumenCompras = () => {
        const { compras } = this.state;

        const totalRegistros = compras.length;
        const totalPEN = compras
            .filter(item => item.moneda === "PEN")
            .reduce((acc, item) => acc + Number(item.total || 0), 0);
        const totalUSD = compras
            .filter(item => item.moneda === "USD")
            .reduce((acc, item) => acc + Number(item.total || 0), 0);
        const totalMonto = totalPEN + totalUSD;
        const promedio = totalRegistros ? totalMonto / totalRegistros : 0;

        const proveedoresMap = new Map();
        compras.forEach(item => {
            const key = item.ruc_proveedor || item.razon_social || "SIN-DATO";
            const actual = proveedoresMap.get(key) || {
                ruc: item.ruc_proveedor,
                razon_social: item.razon_social,
                cantidad: 0,
                monto: 0,
            };
            actual.cantidad += 1;
            actual.monto += Number(item.total || 0);
            proveedoresMap.set(key, actual);
        });

        const proveedores = Array.from(proveedoresMap.values());
        const topProveedor = proveedores.length
            ? proveedores.sort((a, b) => b.monto - a.monto)[0]
            : null;

        return {
            totalRegistros,
            totalPEN,
            totalUSD,
            totalMonto,
            promedio,
            proveedoresUnicos: proveedores.length,
            topProveedor,
        };
    }

    renderCardsResumen = () => {
        const resumen = this.getResumenCompras();

        const cardStyle = {
            border: `1px solid ${lightColor}`,
            borderLeft: `5px solid ${secondaryColor}`,
            borderRadius: "10px",
            boxShadow: "0 3px 10px rgba(0,0,0,0.06)",
            background: "transparent",
        };

        const titleStyle = {
            color: primaryColor,
            fontSize: "0.8rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
        };

        const valueStyle = {
            color: secondaryColor,
            fontSize: "1.25rem",
            fontWeight: 700,
            marginBottom: 0,
        };

        const infoIconStyle = {
            display: "inline-block",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: secondaryColor,
            color: "white",
            fontSize: "0.7rem",
            lineHeight: "16px",
            textAlign: "center",
            marginLeft: "5px",
            cursor: "help",
        };

        return (
            <div className="row mb-3">
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Total Registros
                                <span style={infoIconStyle} title="Cantidad de comprobantes que los proveedores han declarado ante SUNAT">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{resumen.totalRegistros}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Monto Total (PEN)
                                <span style={infoIconStyle} title="Suma total en soles según declaración de SUNAT - SIRE">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{this.formatMoney(resumen.totalPEN, "PEN")}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Monto Total (USD)
                                <span style={infoIconStyle} title="Suma total en dólares según declaración de SUNAT - SIRE">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{this.formatMoney(resumen.totalUSD, "USD")}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Promedio por Comprobante
                                <span style={infoIconStyle} title="Monto promedio de los comprobantes declarados en SUNAT">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{this.formatMoney(resumen.promedio, "PEN")}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Proveedores Únicos
                                <span style={infoIconStyle} title="Cantidad de proveedores diferentes en las compras declaradas">ℹ️</span>
                            </div>
                            <p style={valueStyle}>{resumen.proveedoresUnicos}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 mb-3">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-body py-2">
                            <div style={titleStyle}>
                                Proveedor con Mayor Monto
                                <span style={infoIconStyle} title="Proveedor con mayor monto acumulado en la declaración SUNAT">ℹ️</span>
                            </div>
                            <div className="font-weight-bold" style={{ color: primaryColor }}>
                                {resumen.topProveedor ? (resumen.topProveedor.razon_social || "SIN NOMBRE") : "Sin datos"}
                            </div>
                            <div className="small" style={{ color: secondaryColor }}>
                                {resumen.topProveedor ? `${resumen.topProveedor.ruc || "SIN RUC"} - ${this.formatMoney(resumen.topProveedor.monto, "PEN")}` : ""}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderComprasTable = () => {
        const { compras } = this.state;

        const columns = [
            {
                columns: [
                    {
                        Header: "Fecha",
                        id: "fecha_emision",
                        accessor: d => d.fecha_emision,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["fecha_emision"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Tipo",
                        id: "tipo_doc",
                        accessor: d => d.tipo_doc,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["tipo_doc"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Serie",
                        id: "serie",
                        accessor: d => d.serie,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["serie"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Numero",
                        id: "numero",
                        accessor: d => d.numero,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["numero"] }),
                        filterAll: true,
                    },
                    {
                        Header: "RUC Proveedor",
                        id: "ruc_proveedor",
                        width: 150,
                        accessor: d => d.ruc_proveedor,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["ruc_proveedor"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Razon Social",
                        id: "razon_social",
                        accessor: d => d.razon_social,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["razon_social"] }),
                        filterAll: true,
                    },
                    {
                        Header: "Total",
                        id: "total",
                        accessor: d => Number(d.total || 0),
                        Cell: props => <div className="text-right">{this.formatMoney(props.original.total, props.original.moneda)}</div>,
                    },
                    {
                        Header: "Moneda",
                        id: "moneda",
                        accessor: d => d.moneda,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["moneda"] }),
                        filterAll: true,
                    }
                ]
            }
        ];

        if (!compras.length) {
            return null;
        }

        return (
            <>
                <div className="py-3 reporte-compras-sire-root">
                    <div className="mb-4 text-center">
                        <h4 className="text-muted mb-2">Reporte SIRE de Compras</h4>
                        <p className="text-muted mb-0">Consulta directa a SUNAT por periodo.</p>
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div
                            className="card-header d-flex justify-content-between align-items-center"
                            style={{
                                background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                color: "#fff",
                            }}
                        >
                            <span className="font-weight-bold">Detalle de Compras SUNAT</span>
                        </div>
                <div className="card-body p-2" style={{ backgroundColor: "transparent" }}>
                    <ReactTable
                        data={compras}
                        filterable
                        columns={columns}
                        defaultPageSize={10}
                        className="-striped -highlight"
                        previousText="Anterior"
                        nextText="Siguiente"
                        loadingText="Cargando..."
                        noDataText="No se encontraron registros"
                        pageText="Pagina"
                        ofText="de"
                        rowsText="filas"
                        pageSizeOptions={[10, 20, 25, 50, 100]}
                        minRows={1}
                    />
                </div>
                </div>
            </div>
            </>
        );
    }

    render() {
        const {
            loadingCompras,
            errorCompras,
            periodoCompras,
            totalCompras,
        } = this.state;

        const disclaimerStyle = {
            backgroundColor: "#fff3cd",
            borderLeft: "4px solid #ffc107",
            padding: "12px 15px",
            borderRadius: "4px",
            marginBottom: "20px",
            fontSize: "0.9rem",
            lineHeight: "1.5",
        };

        const aclaracionImportanteStyle = {
            backgroundColor: "#ffe6e6",
            border: "2px solid #dc3545",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "20px",
            lineHeight: "1.6",
        };

        const bannerStyle = {
            backgroundColor: "#e7f3ff",
            border: "2px solid #0066cc",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "20px",
            textAlign: "center",
            fontWeight: "bold",
            color: "#0066cc",
        };

        return (
            <>
                <div className="py-3 reporte-compras-sire-root">
                    {/* Banner con origen de datos */}
                    <div style={bannerStyle}>
                        <span style={{ fontSize: "1.2rem", marginRight: "8px" }}>☁️</span>
                        DATOS PROVENIENTES DE SUNAT - CONSULTA DIRECTA A SIRE
                    </div>

                    <div className="mb-4 text-center">
                        <h4 className="text-muted mb-2">Reporte de Compras - Consulta SUNAT SISTEMA INTEGRADO DE REGISTROS ELECTRONICOS (SIRE)</h4>
                        <p className="text-muted mb-0">Información extraída directamente de los registros electrónicos de SUNAT. NO corresponde a movimientos registrados en el sistema interno.</p>
                    </div>

                    {/* Aclaración Importante */}
                    <div style={aclaracionImportanteStyle}>
                        <h6 style={{ color: "#dc3545", marginBottom: "10px", fontWeight: "bold" }}>⚠️ ACLARACIÓN IMPORTANTE</h6>
                        <ul style={{ marginBottom: "0", paddingLeft: "20px" }}>
                            <li><strong>Estos datos NO son comparables con sus registros internos.</strong> SUNAT muestra lo que los <strong>PROVEEDORES han declarado</strong>, no lo que usted ha registrado.</li>
                            <li>Posibles diferencias: compras no registradas aún en el sistema, fechas de comprobante diferentes a fechas de ingreso, montos que no cuadran con su contabilidad.</li>
                            <li>Use este reporte únicamente para <strong>reconciliación fiscal con SUNAT</strong>, no para validar compras internas.</li>
                        </ul>
                    </div>

                    <div className="alert alert-info mb-4" role="alert">
                        <strong>ℹ️ Información Importante:</strong> Este reporte es solamente informativo y extraído de los registros de SIRE SUNAT por lo que no se debe de comparar con los registros hechos en el sistema, esta información es de carácter especial y de manejo contable.
                    </div>

                    <div className="card border-0 shadow-sm mb-4">
                        <div
                            className="card-header d-flex justify-content-between align-items-center"
                            style={{
                                background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                color: "#fff",
                            }}
                        >
                            <h5 className="mb-0">Compras SUNAT</h5>
                            <span className="badge badge-light" style={{ color: primaryColor }}>{totalCompras} registros</span>
                        </div>
                        <div className="card-body">
                            <div className="form-row align-items-end mb-3">
                                <div className="col-md-3 mb-2 mb-md-0">
                                    <label className="mb-1">Periodo</label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={this.periodoToMonth(periodoCompras)}
                                        onChange={(e) => this.setState({ periodoCompras: this.monthToPeriodo(e.target.value) })}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button className="btn btn-primary btn-block" onClick={this.consultarCompras} disabled={loadingCompras}>
                                        {loadingCompras ? "Consultando..." : "Consultar SUNAT"}
                                    </button>
                                </div>
                            </div>

                            {errorCompras ? <div className="alert alert-danger">{errorCompras}</div> : null}
                            {loadingCompras ? <div className="text-muted"><strong>⏳ Obteniendo datos de SUNAT...</strong></div> : this.renderCardsResumen()}
                            {loadingCompras ? null : this.renderComprasTable()}
                        </div>
                    </div>

                    {/* Disclaimer al pie */}
                    <div style={disclaimerStyle}>
                        <strong>📌 Nota:</strong> La información mostrada corresponde exclusivamente a los registros electrónicos declarados por proveedores ante SUNAT. Para ver sus movimientos internos de compras, diríjase al módulo de Compras del sistema.
                    </div>
                </div>            
            </>
        )
    }
}

export default ReporteComprasSire;